import { forwardRef, useEffect, useRef, useState } from "react";
import { FiChevronDown, FiLock, FiCalendar, FiCheck } from "react-icons/fi";
import { toast } from "react-toastify";
import OutsideClickHandler from "react-outside-click-handler/build/OutsideClickHandler";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import TimeOffService from "src/Services/API/timeoff.service";
import UserService from "src/Services/API/user.service";
import { handleError } from "src/Common/function";
import { TYPE_META, dayCount } from "../TimeOff/TimeOff";

// type options, in display order, derived from the shared TYPE_META map
const TYPE_OPTIONS = [0, 1, 2, 3].map((value) => ({ value, label: TYPE_META[value].label }));

// "YYYY-MM-DD" in local time — matches the value format of <input type="date">
const todayIso = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

// "YYYY-MM-DD" from any backend date value (date columns can arrive tz-shifted,
// so read calendar parts in local time rather than slicing toISOString()).
const toInputDate = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

// "YYYY-MM-DD" string -> local Date (midnight), and back. The form keeps dates
// as ISO strings; react-datepicker works in Date objects, so we convert at the edge.
const isoToDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const dateToIso = (date) => {
  if (!date) return "";
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${m}-${day}`;
};

// Trigger styled to match the form's other fields, used as react-datepicker's customInput.
const DateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <button
    type="button"
    ref={ref}
    onClick={onClick}
    className="flex w-full items-center justify-between h-10 pl-3 pr-3 rounded-md border border-[#E5E8F3] bg-white text-left text-[14px] font-OpenSansRegular leading-5 tracking-[0.14px]"
  >
    <span className={value ? "text-[#1A2332]" : "text-[#868DA6]"}>{value || placeholder}</span>
    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
      <path
        d="M4.16667 3.95833V0.625C4.16667 0.279822 4.44649 0 4.79167 0C5.13684 0 5.41667 0.279822 5.41667 0.625V3.95833C5.41667 4.30351 5.13684 4.58333 4.79167 4.58333C4.44649 4.58333 4.16667 4.30351 4.16667 3.95833Z"
        fill="#868DA6"
      />
      <path
        d="M10.8333 3.95833V0.625C10.8333 0.279822 11.1132 0 11.4583 0C11.8035 0 12.0833 0.279822 12.0833 0.625V3.95833C12.0833 4.30351 11.8035 4.58333 11.4583 4.58333C11.1132 4.58333 10.8333 4.30351 10.8333 3.95833Z"
        fill="#868DA6"
      />
      <path
        d="M15 3.95833C15 3.38304 14.5336 2.91667 13.9583 2.91667H2.29167C1.71637 2.91667 1.25 3.38304 1.25 3.95833V15.625C1.25 16.2003 1.71637 16.6667 2.29167 16.6667H13.9583C14.5336 16.6667 15 16.2003 15 15.625V3.95833ZM16.25 15.625C16.25 16.8907 15.224 17.9167 13.9583 17.9167H2.29167C1.02601 17.9167 0 16.8907 0 15.625V3.95833C0 2.69268 1.02601 1.66667 2.29167 1.66667H13.9583C15.224 1.66667 16.25 2.69268 16.25 3.95833V15.625Z"
        fill="#868DA6"
      />
      <path
        d="M15.625 6.66667C15.9702 6.66667 16.25 6.94649 16.25 7.29167C16.25 7.63684 15.9702 7.91667 15.625 7.91667H0.625C0.279822 7.91667 0 7.63684 0 7.29167C0 6.94649 0.279822 6.66667 0.625 6.66667H15.625Z"
        fill="#868DA6"
      />
    </svg>{" "}
  </button>
));
DateInput.displayName = "DateInput";

// Modal for creating a new time-off request, or editing an existing pending one
// when `editItem` is provided. Calls onSaved() after a successful write so the
// parent can refresh its list.
function NewTimeOffModal({ shown, editItem, onClose, onSaved }) {
  const currentUser = UserService.getCurrentUser();
  const isEdit = !!editItem;

  const [type, setType] = useState(0);
  const [singleDay, setSingleDay] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [notes, setNotes] = useState("");
  const [typeOpen, setTypeOpen] = useState(false);
  const [typeTouched, setTypeTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const typeRef = useRef(null);

  // (re)initialise the form whenever the modal opens, from editItem if editing
  useEffect(() => {
    if (!shown) return;
    if (editItem) {
      setType(editItem.type ?? 0);
      const s = toInputDate(editItem.startDate);
      const e = toInputDate(editItem.endDate);
      setStartDate(s);
      setEndDate(e);
      setSingleDay(!!s && s === e);
      setNotes(editItem.notes || "");
      setTypeTouched(true);
    } else {
      setType(0);
      setSingleDay(false);
      setStartDate("");
      setEndDate("");
      setNotes("");
      setTypeTouched(false);
    }
    setTypeOpen(false);
  }, [shown, editItem]);

  // keep endDate in sync: mirror start in single-day mode, and never let it
  // precede the start date in range mode.
  useEffect(() => {
    if (singleDay) {
      setEndDate(startDate);
    } else if (startDate && endDate && endDate < startDate) {
      setEndDate(startDate);
    }
  }, [singleDay, startDate, endDate]);

  if (!shown) return null;

  const effectiveEnd = singleDay ? startDate : endDate;
  // only count once both ends are known; an unset end date isn't "1 day"
  const days = startDate && effectiveEnd ? dayCount(startDate, effectiveEnd) : 0;
  const canSubmit = typeTouched && !!startDate && !!effectiveEnd && !submitting;

  const handleSubmit = () => {
    if (!typeTouched) {
      toast.error("Please select a time off type.");
      return;
    }
    if (!startDate || !effectiveEnd) {
      toast.error("Please select your dates.");
      return;
    }
    if (effectiveEnd < startDate) {
      toast.error("End date can't be before the start date.");
      return;
    }
    if (!currentUser?.id) {
      toast.error("Unable to determine the current user.");
      return;
    }

    setSubmitting(true);
    const trimmedNotes = notes.trim();
    const request = isEdit
      ? TimeOffService.update(editItem.id, {
          type,
          startDate,
          endDate: effectiveEnd,
          notes: trimmedNotes || null,
        })
      : TimeOffService.create({
          type,
          startDate,
          endDate: effectiveEnd,
          notes: trimmedNotes || undefined,
          userId: currentUser.id,
        });

    request
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          toast.success(isEdit ? "Time off request updated" : "Time off request submitted");
          onSaved?.();
          onClose?.();
        }
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const selectedType = typeTouched ? TYPE_OPTIONS.find((o) => o.value === type) : null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30 px-4">
      <OutsideClickHandler onOutsideClick={() => !submitting && onClose?.()}>
        <div className="w-[640px] max-w-full rounded-xl bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] overflow-hidden">
          {/* Header */}
          <div className="flex items-start justify-between px-6 py-4 border-b border-[#E5E8F3]">
            <div className="flex flex-col gap-1">
              <h2 className="text-[20px] font-OpenSansSemiBold text-[#1A2332] leading-7 capitalize">
                {isEdit ? "Edit Time Off Request" : "Create Time Off Request"}
              </h2>
              <p className="text-[14px] font-OpenSansRegular text-[#6B7280] leading-5 tracking-[0.01px]">
                Submit a new request for time away from work.
              </p>
            </div>
            <button
              onClick={() => !submitting && onClose?.()}
              className="flex items-center justify-center w-8 h-8 rounded-full text-[#868DA6] hover:bg-[#F8F9FE] text-[20px] leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>

          {/* Body */}
          <div className="flex flex-col gap-8 p-8 overflow-hidden">
            {/* Requesting For (locked) */}
            <div className="flex flex-col gap-1">
              <label className="text-[14px] font-OpenSansSemiBold text-[#1A2332] leading-5 capitalize tracking-[0.14px]">
                Requesting For
              </label>
              <div className="flex items-center gap-3 h-10 px-3 rounded-md outline outline-1 -outline-offset-1 outline-[#E5E8F3] bg-[#F8F9FE]">
                <span className="flex-1 text-[14px] font-OpenSansRegular text-[#5F6477] leading-5 tracking-[0.14px]">
                  {currentUser?.name || currentUser?.email || "You"} (You)
                </span>
                <FiLock size={16} className="text-[#5F6477]" />
              </div>
            </div>

            {/* Time Off Type */}
            <div className="flex flex-col gap-1">
              <label className="text-[14px] font-OpenSansSemiBold text-[#1A2332] leading-5 capitalize tracking-[0.14px]">
                Time Off Type
              </label>
              <div className="relative" ref={typeRef}>
                <button
                  type="button"
                  onClick={() => setTypeOpen((v) => !v)}
                  className="flex w-full items-center gap-3 h-10 px-3 rounded-md border border-[#E5E8F3] bg-white hover:bg-[#F8F9FE]"
                >
                  {selectedType ? (
                    <span className="flex flex-1 items-center gap-2 text-[14px] font-OpenSansRegular text-[#1A2332] leading-5 tracking-[0.14px]">
                      <span className={`w-1.5 h-1.5 rounded-full ${TYPE_META[selectedType.value].dotClass}`} />
                      {selectedType.label}
                    </span>
                  ) : (
                    <span className="flex-1 text-left text-[14px] font-OpenSansRegular text-[#868DA6] leading-5 tracking-[0.14px]">
                      Select type
                    </span>
                  )}
                  <FiChevronDown
                    size={20}
                    className={`text-[#868DA6] transition-transform duration-150 ${typeOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {typeOpen && (
                  <OutsideClickHandler onOutsideClick={() => setTypeOpen(false)}>
                    <div className="absolute left-0 right-0 top-full mt-1 rounded-md outline outline-1 -outline-offset-1 outline-[#E5E8F3] bg-white shadow-[0_10px_15px_-3px_rgba(0,0,0,0.10)] overflow-hidden z-10">
                      {TYPE_OPTIONS.map((opt, i) => (
                        <div key={opt.value}>
                          {i > 0 && <div className="h-px bg-[#E5E8F3]" />}
                          <button
                            type="button"
                            onClick={() => {
                              setType(opt.value);
                              setTypeTouched(true);
                              setTypeOpen(false);
                            }}
                            className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-[14px] font-OpenSansRegular text-[#1A2332] leading-5 tracking-[0.14px] hover:bg-[#F8F9FE] ${
                              typeTouched && opt.value === type ? "bg-[#F8F9FE]" : ""
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${TYPE_META[opt.value].dotClass}`} />
                            {opt.label}
                          </button>
                        </div>
                      ))}
                    </div>
                  </OutsideClickHandler>
                )}
              </div>
            </div>

            {/* Single Day Request */}
            <button
              type="button"
              onClick={() => setSingleDay((v) => !v)}
              className="flex items-start gap-2 self-start focus:outline-none"
            >
              <span className="py-0.5">
                <span
                  className={`flex items-center justify-center w-4 h-4 rounded border ${
                    singleDay ? "bg-[#0065FD] border-[#0065FD]" : "bg-white border-[#C9CEE2]"
                  }`}
                >
                  {singleDay && <FiCheck size={12} className="text-white" />}
                </span>
              </span>
              <span className="text-[14px] font-OpenSansSemiBold text-[#1A2332] leading-5 capitalize tracking-[0.14px]">
                Single Day Request
              </span>
            </button>

            {/* Dates */}
            <div className="flex gap-4">
              <div className="flex-1 flex flex-col gap-1">
                <label className="text-[14px] font-OpenSansSemiBold text-[#1A2332] leading-5 capitalize tracking-[0.14px]">
                  {singleDay ? "Date" : "Start Date"}
                </label>
                <div className="relative">
                  <DatePicker
                    selected={isoToDate(startDate)}
                    onChange={(date) => setStartDate(dateToIso(date))}
                    minDate={isEdit ? undefined : isoToDate(todayIso())}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select date"
                    wrapperClassName="w-full"
                    popperClassName="z-[70]"
                    popperPlacement="bottom-start"
                    customInput={<DateInput placeholder="Select date" />}
                  />
                </div>
              </div>
              {!singleDay && (
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[14px] font-OpenSansSemiBold text-[#1A2332] leading-5 capitalize tracking-[0.14px]">
                    End Date
                  </label>
                  <div className="relative">
                    <DatePicker
                      selected={isoToDate(endDate)}
                      onChange={(date) => setEndDate(dateToIso(date))}
                      minDate={isoToDate(startDate) || (isEdit ? undefined : isoToDate(todayIso()))}
                      dateFormat="yyyy-MM-dd"
                      placeholderText="Select date"
                      wrapperClassName="w-full"
                      popperClassName="z-[70]"
                      popperPlacement="bottom-start"
                      customInput={<DateInput placeholder="Select date" />}
                    />
                  </div>
                </div>
              )}
            </div>

            {days > 0 && (
              <div className="-mt-6 text-[13px] font-OpenSansRegular text-[#6B7280]">
                {days} {days === 1 ? "day" : "days"} requested
              </div>
            )}

            {/* Reason / Notes */}
            <div className="flex flex-col gap-1">
              <label className="text-[14px] font-OpenSansSemiBold text-[#1A2332] leading-5 capitalize tracking-[0.14px]">
                Reason / Notes
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Add details for your manager..."
                className="h-24 px-3 py-3 rounded-md border border-[#E5E8F3] bg-[#FBFCFF] text-[14px] font-OpenSansRegular text-[#1A2332] leading-5 tracking-[0.14px] placeholder:text-[#868DA6] resize-none"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-4 px-6 py-4 border-t border-[#E5E8F3]">
            <button
              type="button"
              onClick={() => !submitting && onClose?.()}
              disabled={submitting}
              className="h-10 px-1 rounded-md text-[16px] font-OpenSansSemiBold text-[#7C829A] leading-6 capitalize hover:text-[#1A2332] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="h-10 px-4 rounded-lg bg-[#0065FD] hover:bg-[#0058DB] text-[16px] font-OpenSansSemiBold text-white leading-6 capitalize disabled:bg-[#98C1FF] disabled:hover:bg-[#98C1FF]"
            >
              {submitting ? "Saving..." : isEdit ? "Save Changes" : "Submit Request"}
            </button>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
}

export default NewTimeOffModal;
